<template>
	<div v-if="templates" class="grid-flex-container mx-2 pt-6">
		<div class="d-flex" style="overflow-y: hidden">
			<div style="width: 35%" class="column mr-2">
				<v-container fluid class="pa-0">
					<div class="searchRow">
						<div style="flex-grow: 1">
							<v-text-field
								v-model="searchTemplates"
								v-debounce:500ms="onTemplateSearchChanged"
								@click:clear="onClearSearchTemplate"
								append-icon="mdi-magnify"
								label="Search"
								class="py-0 my-0 mb-2 mr-2"
								single-line
								hide-details
								style="max-width: 100%"
								clearable
							>
							</v-text-field>
						</div>
						<v-btn
							v-if="canAddTemplates"
							color="primary"
							@click.stop="addTemplate"
							class="mb-2 pl-4 pr-6 float-right"
						>
							<v-icon> mdi-plus</v-icon>&nbsp;&nbsp;Add
						</v-btn>
					</div>
				</v-container>
				<div style="flex-grow: 1; overflow-y: hidden">
					<v-list dense style="height: 100%">
						<v-list-item-group
							v-model="selectedTemplateIndex"
							style="overflow-y: scroll; height: 100%"
							active-class="primary white--text"
							mandatory
						>
							<v-list-item
								@click.stop="onSelectTemplate(item)"
								v-for="(item, i) in filteredItems"
								:key="i"
								><v-list-item-title>{{
									item.Name + ' - ' + item.EntityName
								}}</v-list-item-title>
								<v-list-item-action @click.stop>
									<v-btn
										v-if="canEditTemplates"
										fab
										small
										icon
										elevation="1"
										class="action-btn"
										@click.stop="onEditTemplate(item)"
									>
										<v-icon small color="primary"
											>mdi-pencil</v-icon
										>
									</v-btn>
								</v-list-item-action>
								<v-list-item-action
									class="ml-1 my-0 mr-0"
									@click.stop
								>
									<v-btn
										v-if="canDeleteTemplates"
										fab
										small
										icon
										elevation="1"
										class="action-btn"
										@click.stop="onDeleteTemplate(item)"
									>
										<v-icon small color="error"
											>mdi-delete</v-icon
										>
									</v-btn>
								</v-list-item-action>
							</v-list-item>
						</v-list-item-group>
					</v-list>
				</div>
			</div>
			<div style="width: 65%" class="column">
				<v-container fluid class="pa-0">
					<div class="searchRow">
						<v-text-field
							v-model="searchTemplateFields"
							append-icon="mdi-magnify"
							label="Search"
							class="py-0 my-0 mb-2 mr-2"
							single-line
							hide-details
						></v-text-field>
						<v-btn
							v-if="canAddTemplateFields"
							color="primary"
							@click.stop="addTemplateField"
							class="mb-2 pl-4 pr-6"
						>
							<v-icon> mdi-plus</v-icon>
							&nbsp;&nbsp;Add
						</v-btn>
						<v-btn
							v-if="templateFields.length == 0"
							color="primary"
							class="ml-2 pl-4 pr-6 float-right"
							@click.stop="uploadFile"
						>
							<v-icon> mdi-plus</v-icon>
							&nbsp;&nbsp;Add from File
						</v-btn>
					</div>
				</v-container>
				<div class="data-table">
					<v-data-table
						dense
						:headers="headers"
						:search="searchTemplateFields"
						:items="templateFields"
						:disable-pagination="true"
						sort-by="Order"
						item-key="Id"
						fixed-header
						:loading="loading || isLoading"
						loading-text="Loading..."
						hide-default-footer
					>
						<template v-slot:body="props">
							<draggable
								v-if="!(loading || isLoading)"
								@change="reorder(props.items)"
								:list="props.items"
								tag="tbody"
							>
								<tr
									:class="getRowCssClass(item)"
									v-for="(item, index) in props.items"
									:key="index"
								>
									<td>
										<v-icon small color="primary"
											>mdi-reorder-horizontal</v-icon
										>
									</td>
									<td>{{ item.Order }}</td>
									<td>{{ item.Label }}</td>
									<td>
										<span v-if="item.GridViewField"
											>[{{
												item.GridViewField.DataField
													.EntityName
											}}].{{
												item.GridViewField.DataField
													.FieldName
											}}</span
										>
									</td>
									<td>
										<span v-if="item.GridViewField">
											<v-icon
												class="mr-2"
												color="primary"
												v-if="
													item.GridViewField
														.LinkFieldId
												"
												>mdi-link-variant</v-icon
											>
											<span
												v-if="
													item.GridViewField
														.LinkFieldId
												"
												>[{{
													item.GridViewField.LinkField
														.EntityName
												}}].{{
													item.GridViewField.LinkField
														.FieldName
												}}</span
											>
										</span>
									</td>
									<td>
										<span v-if="item.GridViewField">
											<v-icon
												class="mr-2"
												color="primary"
												v-if="
													item.GridViewField
														.ReferenceListTypeId
												"
												>mdi-form-dropdown</v-icon
											>
											<span
												v-if="
													item.GridViewField
														.ReferenceListTypeId
												"
												>{{
													item.GridViewField
														.ReferenceListType
												}}
												- [{{
													item.GridViewField.DataField
														.EntityName
												}}].{{
													item.GridViewField
														.DataFieldName
												}}</span
											>
										</span>
									</td>
									<td>
										<v-icon
											color="primary"
											v-if="
												item.GridViewField &&
												item.Confirmed
											"
											>mdi-check-circle</v-icon
										>
										<v-switch
											v-else-if="
												item.GridViewField &&
												!item.Confirmed
											"
											v-model="item.Confirmed"
											@change="
												onConfirmGridViewField(item)
											"
										/>
										<v-icon color="error" v-else
											>mdi-close-circle</v-icon
										>
									</td>
									<td>
										<v-btn
											v-if="canDeleteTemplateFields"
											fab
											small
											icon
											elevation="1"
											class="action-btn float-right"
											@click.stop="
												onDeleteTemplateFields(item)
											"
										>
											<v-icon color="error" small
												>mdi-delete</v-icon
											>
										</v-btn>
										<v-btn
											v-if="canEditTemplateFields"
											fab
											small
											icon
											elevation="1"
											class="action-btn float-right"
											@click.stop="
												onEditTemplateField(item)
											"
										>
											<v-icon color="primary" small
												>mdi-pencil</v-icon
											>
										</v-btn>
									</td>
								</tr>
							</draggable>
						</template>
					</v-data-table>
				</div>
			</div>
		</div>
		<template-dialog
			v-if="showTemplateDialog"
			:showDialog="showTemplateDialog"
			:template="editTemplate"
			@close="closeTemplateDialog"
		/>
		<template-field-modal
			v-if="showTemplateFieldModal && selectedTemplate"
			:selectedTemplate="selectedTemplate"
			:editField="selectedTemplateField"
			:showDialog="showTemplateFieldModal"
			@close="closeTemplateFieldModal"
		/>
		<file-upload-modal
			v-if="showFileUploadModal && selectedTemplate"
			:selectedTemplate="selectedTemplate"
			:showDialog="showFileUploadModal"
			@close="closeFileUploadModal"
		/>
	</div>
</template>

<script>
import { TemplateActions } from '../../../store/actions';
import TemplateDialog from './TemplateDialog.vue';
import permissionsMixin from '../../../mixins/permissionsMixin';
import TemplateFieldModal from './TemplateFieldModal.vue';
import FileUploadModal from './FileUploadModal.vue';
import Draggable from 'vuedraggable';

import templatesMixin from '../../../mixins/dataImportTemplate/templatesMixin';
import templateFieldsMixin from '../../../mixins/dataImportTemplate/templateFieldsMixin';
export default {
	name: 'DataImportTemplatesNew',
	mixins: [permissionsMixin, templatesMixin, templateFieldsMixin],
	components: {
		TemplateDialog,
		Draggable,
		TemplateFieldModal,
		FileUploadModal,
	},
	data() {
		return {
			searchTemplateFields: '',
			showTemplateFieldModal: false,
			isLoading: false,
			clearListTrigger: '',
			showFileUploadModal: false,
		};
	},
	destroyed() {
		this.$store.dispatch(TemplateActions.CLEAR);
	},
	methods: {
		uploadFile() {
			this.showFileUploadModal = true;
		},

		closeFileUploadModal() {
			this.showFileUploadModal = false;
		},

		reorder(props) {
			let reorderedValues = props;
			reorderedValues.forEach((element) => {
				element.Order = reorderedValues.indexOf(element) + 1;
				if (element.GridViewField) {
					element.GridViewField.Order = element.Order;
				}
			});
			this.$store.dispatch(
				TemplateActions.REORDER_COLUMNS,
				reorderedValues
			);
		},
	},
};
</script>
<style lang="scss">
.data-table {
	flex-grow: 1;
	overflow-y: hidden;
}
.searchRow {
	display: flex;
	flex-direction: row;
}
.column {
	display: flex;
	overflow-y: hidden;
	height: 100%;
	flex-direction: column;
}

tr.rowYellow:hover {
	background-color: #ffc00030 !important;
	border-color: #ffc00030 !important;
}
tr.rowYellow {
	background-color: #ffc00030;
	border-color: #ffc00030;
}

tr.rowRed:hover {
	background-color: #ff343480 !important ;
	border-color: #ff343480 !important;
}
tr.rowRed {
	background-color: #ff343480;
	border-color: #ff343480;
}

.v-input--switch {
	margin: 0;
	padding: 0;
	.v-input__slot {
		margin-bottom: 0;
	}
	.v-messages {
		display: none;
	}
}
</style>
